import {translate} from "react-i18nify";

import * as componentTypes from "../constants/componentTypes";
import * as resultCodes from "../constants/resultCodes";
import * as license from "../constants/license";
import * as actionTypes from "../constants/actionTypes";
import * as modelHelper from "../helpers/modelHelper";
import * as translationHelper from "../helpers/translationHelper";
import * as windowService from "../services/windowService";

const getServerResponseMessage = (response) => {
    if (response.msg !== undefined) {
        return translate("flashMessage.serverError") + ': "' + response.msg + '"';
    }

    return translate("flashMessage.defaultMessage");
};

export const getMatchingFlashMessageByResult = (response) => {
    if (response.msgParams === undefined) {
        return modelHelper.getWarningFlashMessage(translationHelper.getMatchingErrorTranslations(response.result));
    }

    return modelHelper.getWarningFlashMessage(
        translationHelper.getMatchingErrorTranslations(response.result, response.msgParams)
    );
};

export const getFlashMessageByResult = (response) => {
    switch (response.result) {
        case resultCodes.RESULT_INVALID_JSON_INPUT:
        case resultCodes.RESULT_COMMAND_TYPE_MISSING:
        case resultCodes.RESULT_PARAMETER_VALIDATION_FAILED:
        case resultCodes.RESULT_UNKNOWN_COMMAND:
        case resultCodes.RESULT_UNKNOWN_ERROR:
            return modelHelper.getErrorFlashMessage(getServerResponseMessage(response));
        case resultCodes.RESULT_INVALID_LICENSE:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.invalidLicenseKey"));
        case resultCodes.RESULT_INVALID_LICENSE_IDENTIFICATION_KEYS:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.invalidLicenseIdentificationKeys"));
        case resultCodes.RESULT_CLIENT_CONNECTION_REJECTED:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.clientConnectionRejected"));
        case resultCodes.RESULT_CLIENT_NOT_LOGGED_IN:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.clientNotLoggedIn"));
        case resultCodes.RESULT_LOGIN_FAILED:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.loginError"));
        case resultCodes.RESULT_LOGOUT_FAILED:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.logoutError"));
        case resultCodes.RESULT_USER_ALREADY_LOGGED_IN:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.userAlreadyLoggedIn"));
        case resultCodes.RESULT_DUPLICATE_NAME:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.duplicateName"));
        case resultCodes.RESULT_INVALID_EMAIL:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.invalidEmail"));
        case resultCodes.RESULT_ITEM_NOT_FOUND:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.itemNotFound"));
        case resultCodes.RESULT_BARCODE_NOT_FOUND:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.barcodeNotFound"));
        case resultCodes.RESULT_DATABASE_AUTOLOGIN_FAILED:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.autologinFailed"));
        case resultCodes.RESULT_INVALID_DATABASE_NAME:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.invalidDatabaseName"));
        case resultCodes.RESULT_REDLIKE_DATA_NOT_AVAILABLE:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.dataNotAvailable"));
        case resultCodes.RESULT_CANNOT_CONNECT_TO_SERVER:
            return modelHelper.getInfoFlashMessage(translate("flashMessage.cannoctConnectToServer"));
        case resultCodes.RESULT_ORDER_HAS_ALREADY_BEEN_ACQUIRED:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.orderAlreadyAcquired"));
        case resultCodes.RESULT_USER_CHANGE_PASSWORD_FAILED:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.incorrectPassword"));
        case resultCodes.RESULT_INVALID_COLORANT:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.invalidFormulaColorants"));
        case resultCodes.RESULT_MALFORMED_FORMULA:
            return modelHelper.getErrorFlashMessage(translate("flashMessage.malformedFormula"));
        case resultCodes.RESULT_IMPORT_ABORTED:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.importAborted"));
        case resultCodes.RESULT_BARCODE_EXISTS:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.duplicateBarcode"));
        case resultCodes.RESULT_CUSTOMER_NOT_FOUND:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.customerNotFound"));
        case resultCodes.RESULT_EMPTY_STOCK:
            return modelHelper.getWarningFlashMessage(translate("flashMessage.emptyStock"));
        case resultCodes.RESULT_TMC_NOT_AVAILABLE:
            return modelHelper.getErrorFlashMessage(
                translate("flashMessage.tmcNotAvailable", {tmc: "TMC", collector: "Collector"})
            );
        case resultCodes.RESULT_TMC_NOT_CONNECTED:
            return modelHelper.getWarningFlashMessage(
                translate("flashMessage.tmcNotConnected", {tmc: "TMC", collector: "Collector"})
            );
        case resultCodes.RESULT_MAX_FILE_SIZE_EXCEEDED:
            return modelHelper.getErrorFlashMessage(
                translate("flashMessage.maxFileSizeExceeded", response?.msgParams ?? ["N/A"])
            );
        default:
            if (typeof response.result === "string") {
                if (response.result.startsWith("M")) {
                    return getMatchingFlashMessageByResult(response);
                }
            }

            return modelHelper.getErrorFlashMessage(getServerResponseMessage(response));
    }
};

export const getFlashMessage = (response) => {
    if (response.result === resultCodes.RESULT_OK) {
        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: modelHelper.getSuccessFlashMessage(translate("general.ok"))
            }
        };
    }

    windowService.logError("Server error   ", JSON.stringify(response));

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: getFlashMessageByResult(response)
        }
    };
};

const getRccResponseMessage = (response) => {
    if (response.msg !== undefined) {
        return translate("flashMessage.rccError") + ': "' + response.msg + '"';
    }

    return translate("flashMessage.rccDefaultMessage");
};

export const getRccFlashMessage = (response) => {
    if (
        response.result === resultCodes.RCC_RESULT_ABORTED ||
        response.result === resultCodes.RCC_RESULT_FORMULA_COLORANT_MISSING ||
        response.result === resultCodes.RCC_RESULT_FORMULA_CHANNEL_MISSING
    ) {
        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: null
            }
        };
    }

    let componentType = null;
    let flashMessage = null;

    windowService.logError("RCC    error   ", JSON.stringify(response));

    switch (response.result) {
        case resultCodes.RCC_RESULT_JSON_INPUT:
        case resultCodes.RCC_RESULT_COMMAND_TYPE_MISSING:
        case resultCodes.RCC_RESULT_PARAMETER_VALIDATION_FAILED:
        case resultCodes.RCC_RESULT_UNKNOWN_COMMAND:
        case resultCodes.RCC_RESULT_UNKNOWN_ERROR:
            flashMessage = getRccResponseMessage(response);
            componentType = componentTypes.FLASH_MESSAGE_ERROR;
            break;
        case resultCodes.RCC_RESULT_AUTOTASK_DUPLICATE_NAME:
            flashMessage = translate("flashMessage.autotaskDuplicateName", [
                response.msgParams[0],
                response.msgParams[1],
                response.msgParams[2],
                response.msgParams[3]
            ]);
            componentType = componentTypes.FLASH_MESSAGE_WARNING;
            break;
        case resultCodes.RCC_RESULT_PRINTING_ERROR:
        case resultCodes.RCC_RESULT_INVALID_LICENSE:
        default:
            flashMessage = getRccResponseMessage(response);
            componentType = componentTypes.FLASH_MESSAGE_ERROR;
            break;
    }

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getFlashMessage(componentType, flashMessage)
        }
    };
};

const getHelperResponseMessage = (response) => {
    if (response.message !== undefined) {
        return translate("flashMessage.helperServerError") + ': "' + response.message + '"';
    }

    return translate("flashMessage.helperDefaultMessage");
};

export const getHelperFlashMessage = (response) => {
    let componentType = null;
    let flashMessage = null;

    windowService.logError("Helper error   ", JSON.stringify(response));

    switch (response.result) {
        case resultCodes.HELPER_RESULT_JSON_DECODE:
        case resultCodes.HELPER_RESULT_JSON_ENCODE:
        case resultCodes.HELPER_RESULT_COMMAND_TYPE_MISSING:
        case resultCodes.HELPER_RESULT_PARAMETER_VALIDATION_FAILED:
        case resultCodes.HELPER_RESULT_UNKNOWN_COMMAND:
            flashMessage = getHelperResponseMessage(response);
            componentType = componentTypes.FLASH_MESSAGE_ERROR;
            break;
        default:
            flashMessage = getHelperResponseMessage(response);
            componentType = componentTypes.FLASH_MESSAGE_ERROR;
            break;
    }

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getFlashMessage(componentType, flashMessage)
        }
    };
};

export const getInvalidFormatMessage = (response) => {
    if (response.result === resultCodes.RESULT_OK) {
        windowService.logWarn("Invalid format of server response: " + JSON.stringify(response));

        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: modelHelper.getErrorFlashMessage(translate("flashMessage.invalidServerResponse"))
            }
        };
    }

    return getFlashMessage(response);
};

export const getRccInvalidFormatMessage = (response) => {
    if (response.result === undefined || response.result === resultCodes.RESULT_OK) {
        windowService.logWarn("Invalid format of RCC response: " + JSON.stringify(response));

        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: modelHelper.getErrorFlashMessage(translate("flashMessage.invalidRccResponse"))
            }
        };
    }

    return getRccFlashMessage(response);
};

export const getInvalidLicenseMessage = (code, defaultMessage) => {
    switch (code) {
        case license.NOT_LICENSED:
            return translate("flashMessage.productNotLicensed");
        case license.INVALID_LICENSE_FILE:
            return translate("flashMessage.invalidLicenseFile");
        case license.LICENSE_FILE_CORRUPTED:
            return translate("flashMessage.corruptedLicenseFile");
        case license.INVALID_KEY:
            return translate("flashMessage.invalidLicenseCompanyUserKey");
        case license.CANNOT_WRITE_LICENSE:
            return translate("flashMessage.cannotWriteLicense");
        case license.LICENSE_EXPIRED:
            return translate("flashMessage.expiredLicense");
        case license.LICENSE_REVOKED:
            return translate("flashMessage.revokedLicense");
        case license.LICENSE_PROLONGATION_NEEDED:
            return translate("flashMessage.licenseProlongationNeeded");
        case license.INVALID_PRODUCT:
            return translate("flashMessage.invalidLicenseProduct");
        case license.FEATURE_NOT_AUTORIZED:
            return translate("flashMessage.featureNotAutorized");
        case license.VERSION_TOO_NEW:
            return translate("flashMessage.versionNotAllowedByLicense");
        case license.COMPUTER_ID_MISMATCH:
        case license.SYSTEM_INFO_MISMATCH:
        case license.CPU_MEMORY_MISMATCH:
        case license.DISKS_MISMATCH:
        case license.NETWORK_MISMATCH:
            return translate("flashMessage.licensedOnDifferentComputer");
        case license.VM_NOT_ALLOWED:
            return translate("flashMessage.VMNotAllowed");
        case license.COMPUTER_BACKDATED:
            return translate("flashMessage.computerBackdated");
        case license.WEB_API_CONNECTION_ERROR:
            return translate("flashMessage.webApiConnectionError");
        case license.WEB_API_INTERNAL_ERROR:
            return translate("flashMessage.webApiInternalError");
        case license.WEB_API_INVALID_USER:
            return translate("flashMessage.webApiInvalidUser");
        case license.WEB_API_NO_LICENSE_AVAILABLE:
            return translate("flashMessage.webApiNoLicenseAvailable");
        case license.DELETED_LICENSE:
            return translate("flashMessage.licenseHasBeenRevoked");
        default:
            return defaultMessage === undefined ? "" : defaultMessage;
    }
};

export const getLicenseErrorMessage = (response) => {
    windowService.logError("License error  ", JSON.stringify(response));

    if (response.data.error === undefined) {
        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: modelHelper.getErrorFlashMessage(translate("flashMessage.invalidServerResponse"))
            }
        };
    }

    if (response.data.error.code === license.UNKNOWN_ERROR) {
        return {
            type: actionTypes.SAVE_FLASH_MESSAGE,
            payload: {
                flashMessage: modelHelper.getErrorFlashMessage(
                    translate("flashMessage.unknownError", {0: response.data.error.message.split(":")[1]})
                )
            }
        };
    }

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getWarningFlashMessage(
                getInvalidLicenseMessage(response.data.error.code, response.data.error.message)
            )
        }
    };
};

export const getInfoMessageAction = (messageKey, args = null) => {
    if (args !== null) {
        if ((Array.isArray(args) && args.length > 0) || (typeof args === "object" && Object.keys(args).length > 0)) {
            return {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getInfoFlashMessage(translate(messageKey, args))
                }
            };
        }
    }

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getInfoFlashMessage(translate(messageKey))
        }
    };
};

export const getSuccessMessageAction = (messageKey) => {
    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getSuccessFlashMessage(translate(messageKey))
        }
    };
};

export const getWarningMessageAction = (messageKey) => {
    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getWarningFlashMessage(translate(messageKey))
        }
    };
};

export const getErrorMessageAction = (messageKey, args = null) => {
    if (args !== null) {
        if ((Array.isArray(args) && args.length > 0) || (typeof args === "object" && Object.keys(args).length > 0)) {
            return {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getErrorFlashMessage(translate(messageKey, args))
                }
            };
        }
    }

    return {
        type: actionTypes.SAVE_FLASH_MESSAGE,
        payload: {
            flashMessage: modelHelper.getErrorFlashMessage(translate(messageKey))
        }
    };
};

export const getFileTransferError = (response) => {
    switch (response.data.code) {
        case resultCodes.FT_UNKNOWN_VERSION:
            return getErrorMessageAction("flashMessage.fileTransferUnknownVersion");
        case resultCodes.FT_INVALID_FILE:
            return getErrorMessageAction("flashMessage.fileTransferInvalidFile");
        default:
            return {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getErrorFlashMessage(response.data.message)
                }
            };
    }
};

export const getFileTransferInError = (response) => {
    switch (response.data.code) {
        case resultCodes.FT_IN_FILE_NOT_PROVIDED:
            return getErrorMessageAction("flashMessage.fileTransferFileNotProvided");
        case resultCodes.FT_IN_INVALID_FILE:
        case resultCodes.FT_IN_UNKNOWN_VERSION:
        case resultCodes.FT_IN_CUSTOMER_ERROR:
        case resultCodes.FT_IN_CUSTOM_FORMULA_ERROR:
        case resultCodes.FT_IN_ORDER_ERROR:
        default:
            return {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getErrorFlashMessage(response.data.message)
                }
            };
    }
};

export const getFileTransferOutError = (response) => {
    switch (response.data.code) {
        case resultCodes.FT_OUT_NO_DATA:
            return getErrorMessageAction("flashMessage.filetransferNoData");
        case resultCodes.FT_OUT_INVALID_ITEM:
        default:
            return {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getErrorFlashMessage(response.data.message)
                }
            };
    }
};
